import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Briefcase, ArrowRight, CheckCircle2, FileDown, Sparkles } from "lucide-react";
import { CORPORATE_PACKAGES, CORPORATE_TIERS, IMAGES } from "@/data/content";
import { API, api } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import EnquiryForm from "@/components/EnquiryForm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const ACCENT = {
  burgundy: { bg: "bg-[#6E2638]", text: "text-[#6E2638]", ring: "border-[#6E2638]" },
  forest:   { bg: "bg-[#1E4637]", text: "text-[#1E4637]", ring: "border-[#1E4637]" },
  gold:     { bg: "bg-[#B08B2E]", text: "text-[#8A6A16]", ring: "border-[#D4AF37]" },
  navy:     { bg: "bg-[#1F3A5F]", text: "text-[#1F3A5F]", ring: "border-[#1F3A5F]" },
  holly:    { bg: "bg-[#9B1B30]", text: "text-[#9B1B30]", ring: "border-[#9B1B30]" },
};

export default function CorporateTours() {
  return (
    <div data-testid="corporate-page">
      {/* HERO */}
      <section className="relative h-[46vh] min-h-[360px] overflow-hidden">
        <img src={IMAGES.parliament} alt="Melbourne CBD" className="absolute inset-0 w-full h-full object-cover sepia-soft" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-12">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">Corporate Walks</div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">The offsite your team will talk about for months.</h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            Five curated walks through Melbourne's CBD — pick the tone your team needs.
            Pricing tiers: <b>$499</b> up to 10 · <b>$799</b> up to 20 · <b>$999</b> up to 30 · <b>$1,299</b> for 40 or more.
          </p>
        </div>
      </section>

      {/* EXPLAINER */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="stamp">Team Days · Client Entertainment · Board Offsites</div>
            <h2 className="serif text-3xl md:text-4xl text-[#6E2638] mt-4">
              A walking experience, tailored to your team.
            </h2>
            <p className="mt-4 text-[#1A1817]/85 leading-relaxed">
              Whether you're rewarding your top performers, entertaining international clients,
              or breaking the mould on your next board day — Martin will personally curate the
              stories, pace and finish point around your team's mood. Three pricing tiers
              depending on how many are coming. Booking is instant online; Martin calls within
              24 hours to lock in the day and details.
            </p>
          </div>
          <div className="card-warm rounded-2xl p-6">
            <Sparkles className="text-[#6E2638]" />
            <div className="serif text-2xl text-[#6E2638] mt-2">Every walk includes</div>
            <ul className="mt-3 space-y-2 text-sm text-[#1A1817]/85">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> 2-hour guided CBD walk</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Pick a group tier: 20 · 30 · 40+ people</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Meet at Federation Square, in front of the big screen</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Weekday or weekend start times</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Pre-brief and finish-venue suggestions on request</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Tax invoice for company records</li>
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGE GRID */}
      <section className="bg-[#F5EFEB] border-y border-[#E5D9C5] py-16" data-testid="corporate-packages">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <OrnamentalDivider label="Choose your walk" />
          <div className="mt-8 rounded-2xl bg-[#6E2638] text-[#FDFBF7] px-6 py-5 flex flex-col md:flex-row md:items-center gap-3" data-testid="corp-combo-banner">
            <span className="serif text-3xl text-[#E7C766]">15% off</span>
            <span className="text-sm md:text-base">
              Book two themes on the same day — a morning walk plus a second with drinks after — and save 15% on both.
              Just add the second theme when you book.
            </span>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mt-10">
            {CORPORATE_PACKAGES.map((p) => (
              <PackageCard key={p.id} pkg={p} />
            ))}
          </div>
        </div>
      </section>

      {/* LOGISTICS + ENQUIRY */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="stamp">Something bespoke?</div>
          <h2 className="serif text-3xl text-[#6E2638] mt-4">Enquire instead.</h2>
          <p className="mt-4 text-[#1A1817]/85 leading-relaxed">
            Group over 40? Need catering, drinks or dinner
            afterwards? Send an enquiry — Martin will personally shape a bespoke corporate
            walk around your team.
          </p>
          <a href={`${API}/brochure/corporate`} className="btn-outline-gold mt-8 inline-flex" data-testid="brochure-corporate">
            <FileDown size={16} /> Download PDF brochure
          </a>
        </div>
        <div className="lg:col-span-3">
          <EnquiryForm tourType="corporate" />
        </div>
      </section>
    </div>
  );
}

function PackageCard({ pkg }) {
  const acc = ACCENT[pkg.accent] || ACCENT.burgundy;
  const [open, setOpen] = useState(false);

  return (
    <div className={`card-warm rounded-2xl p-7 flex flex-col ${acc.ring} border-l-4`} data-testid={`corporate-package-${pkg.id}`}>
      <div>
        <div className={`inline-block text-[10px] font-bold uppercase tracking-[0.24em] px-3 py-1 rounded-full ${acc.bg} text-[#FDFBF7]`}>
          Corporate Walk
        </div>
        {pkg.seasonal && (
          <div className="inline-flex items-center gap-1 ml-2 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-[#1E4637] text-[#E7C766]" data-testid={`corp-seasonal-${pkg.id}`}>
            <Sparkles size={11} /> {pkg.seasonal}
          </div>
        )}
        <h3 className="serif text-2xl md:text-3xl text-[#6E2638] mt-3 leading-snug">{pkg.title}</h3>
        <p className="text-sm mt-1 italic text-[#1A1817]/70">{pkg.tagline}</p>
      </div>

      <p className="mt-4 text-[15px] text-[#1A1817]/85 leading-relaxed flex-1">{pkg.description}</p>

      <ul className="mt-5 space-y-1.5 text-sm text-[#1A1817]/85">
        {pkg.highlights.map((h, i) => (
          <li key={i} className="flex gap-2">
            <CheckCircle2 size={14} className={`shrink-0 mt-1 ${acc.text}`} />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {pkg.stops && (
        <details className="mt-4 text-sm" data-testid={`corp-stops-${pkg.id}`}>
          <summary className={`cursor-pointer serif ${acc.text}`}>See the route ({pkg.stops.length} stops)</summary>
          <ol className="mt-2 ml-5 list-decimal space-y-1 text-[#1A1817]/80">
            {pkg.stops.map((s) => <li key={s}>{s}</li>)}
          </ol>
        </details>
      )}

      <div className="mt-5 rounded-xl bg-[#FDFBF7] border border-[#E5D9C5] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#1E4637] flex items-center justify-between">
          <span>Pricing tiers</span>
          <Briefcase size={14} className={`${acc.text} opacity-60`} />
        </div>
        <div className="mt-2 space-y-1.5">
          {CORPORATE_TIERS.map((t) => (
            <div key={t.id} className="flex items-baseline justify-between text-sm">
              <span className="text-[#1A1817]/85">{t.label}</span>
              <span className={`serif text-lg ${acc.text}`}>${t.price_aud.toLocaleString()}</span>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-[#1A1817]/60 mt-2">AUD · pick your tier at checkout</div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="btn-primary mt-6 w-full justify-center" data-testid={`book-corp-${pkg.id}`}>
            Book this walk <ArrowRight size={16} />
          </button>
        </DialogTrigger>
        <BookingDialog pkg={pkg} onClose={() => setOpen(false)} />
      </Dialog>
    </div>
  );
}

function BookingDialog({ pkg, onClose }) {
  const [form, setForm] = useState({
    tier_id: CORPORATE_TIERS[0].id,
    company_name: "", contact_name: "", contact_email: "", contact_phone: "",
    preferred_date: "", attendee_count: "", notes: "", second_package_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tier = CORPORATE_TIERS.find((t) => t.id === form.tier_id) || CORPORATE_TIERS[0];
  const combo = !!form.second_package_id;
  const subtotal = tier.price_aud * (combo ? 2 : 1);
  const discount = combo ? Math.round(subtotal * 0.15 * 100) / 100 : 0;
  const total = subtotal - discount;
  const fmt = (n) => n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Smart nudge: if attendee count exceeds selected tier, suggest the smallest fitting tier
  const attendees = Number(form.attendee_count || 0);
  const overCapacity = attendees > 0 && attendees > tier.max;
  const suggestedTier = overCapacity
    ? CORPORATE_TIERS.find((t) => t.max >= attendees) || CORPORATE_TIERS[CORPORATE_TIERS.length - 1]
    : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      toast.error("Company, contact name and email are required");
      return;
    }
    if (combo && !form.preferred_date) {
      toast.error("Pick the date for both walks to get the 15% discount");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        package_id: pkg.id,
        second_package_id: form.second_package_id || null,
        tier_id: form.tier_id,
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        preferred_date: form.preferred_date || null,
        attendee_count: form.attendee_count ? Number(form.attendee_count) : null,
        notes: form.notes || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/corporate/bookings", payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Payment setup didn't return a checkout URL.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid={`corp-booking-dialog-${pkg.id}`}>
      <DialogHeader>
        <DialogTitle className="serif text-2xl text-[#6E2638]">
          Book · {pkg.title}
        </DialogTitle>
        <DialogDescription>
          Pick your group tier, then complete secure Stripe checkout. Martin calls within 24 hours to lock in details.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        {/* TIER PICKER */}
        <div>
          <Label>Group size *</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1" data-testid={`corp-tier-picker-${pkg.id}`}>
            {CORPORATE_TIERS.map((t) => {
              const selected = form.tier_id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => update("tier_id", t.id)}
                  data-testid={`corp-tier-${pkg.id}-${t.id}`}
                  className={`rounded-xl border-2 p-3 text-center transition-all ${selected ? "border-[#6E2638] bg-[#F5EFEB]" : "border-[#E5D9C5] bg-[#FDFBF7] hover:border-[#6E2638]/40"}`}
                >
                  <div className={`serif text-lg ${selected ? "text-[#6E2638]" : "text-[#1A1817]"}`}>${t.price_aud.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#1E4637] mt-0.5">{t.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label htmlFor={`c-${pkg.id}-company`}>Company name *</Label>
          <Input id={`c-${pkg.id}-company`} data-testid={`corp-company-${pkg.id}`} value={form.company_name}
            onChange={(e) => update("company_name", e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`c-${pkg.id}-name`}>Your name *</Label>
            <Input id={`c-${pkg.id}-name`} data-testid={`corp-contact-${pkg.id}`} value={form.contact_name}
              onChange={(e) => update("contact_name", e.target.value)} required />
          </div>
          <div>
            <Label htmlFor={`c-${pkg.id}-email`}>Email *</Label>
            <Input id={`c-${pkg.id}-email`} type="email" data-testid={`corp-email-${pkg.id}`} value={form.contact_email}
              onChange={(e) => update("contact_email", e.target.value)} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`c-${pkg.id}-phone`}>Phone (optional)</Label>
            <Input id={`c-${pkg.id}-phone`} value={form.contact_phone}
              onChange={(e) => update("contact_phone", e.target.value)} />
          </div>
          <div>
            <Label htmlFor={`c-${pkg.id}-count`}>Approx. attendees</Label>
            <Input id={`c-${pkg.id}-count`} type="number" min={1} max={200} value={form.attendee_count}
              onChange={(e) => update("attendee_count", e.target.value)} placeholder="e.g. 25" />
          </div>
        </div>

        {/* SMART TIER NUDGE */}
        {suggestedTier && suggestedTier.id !== form.tier_id && (
          <div className="rounded-xl bg-[#FDF6E3] border border-[#D4AF37] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
               data-testid={`corp-tier-nudge-${pkg.id}`}>
            <div className="flex-1">
              <div className="serif text-sm text-[#6E2638] font-semibold">
                {attendees} attendees is above the {tier.label.toLowerCase()} tier.
              </div>
              <div className="text-xs text-[#1A1817]/75 mt-0.5">
                Bump to <b>{suggestedTier.label}</b> to lock in the correct rate.
              </div>
            </div>
            <button
              type="button"
              onClick={() => update("tier_id", suggestedTier.id)}
              data-testid={`corp-upgrade-tier-${pkg.id}`}
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#6E2638] text-[#FDFBF7] px-4 py-2 text-xs font-semibold hover:bg-[#6E2638]/90"
            >
              Upgrade to ${suggestedTier.price_aud.toLocaleString()}
            </button>
          </div>
        )}
        <ComboPicker pkg={pkg} value={form.second_package_id} onChange={(v) => update("second_package_id", v)} />
        <div>
          <Label htmlFor={`c-${pkg.id}-date`}>{combo ? "Date for both walks *" : "Preferred date (Martin will confirm)"}</Label>
          <Input id={`c-${pkg.id}-date`} type="date" value={form.preferred_date} required={combo}
            data-testid={`corp-date-${pkg.id}`}
            onChange={(e) => update("preferred_date", e.target.value)} />
        </div>
        <div>
          <Label htmlFor={`c-${pkg.id}-notes`}>Anything Martin should know? (finish venue, dietary, timing…)</Label>
          <Textarea id={`c-${pkg.id}-notes`} rows={3} maxLength={500}
            placeholder="e.g. Finishing at Young &amp; Jackson's for drinks · client from overseas · 5pm start preferred"
            value={form.notes} onChange={(e) => update("notes", e.target.value)} />
        </div>
        <div className="rounded-xl bg-[#F5EFEB] border border-[#E5D9C5] p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">You'll pay</div>
            <div className="serif text-2xl text-[#6E2638]" data-testid={`corp-total-${pkg.id}`}>
              AUD ${fmt(total)}
            </div>
            {combo && (
              <div className="text-xs text-[#1E4637] mt-0.5" data-testid={`corp-discount-${pkg.id}`}>
                2 walks · <s>${fmt(subtotal)}</s> · 15% off saves ${fmt(discount)}
              </div>
            )}
            <div className="text-xs text-[#1A1817]/70 mt-1">{tier.label} · Secured via Stripe · Tax invoice included</div>
          </div>
          <Briefcase className="text-[#6E2638] opacity-70" size={32} />
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid={`corp-submit-${pkg.id}`}>
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirecting to secure checkout…</> :
            <>Continue to payment <ArrowRight size={16} /></>}
        </button>
      </form>
    </DialogContent>
  );
}

function ComboPicker({ pkg, value, onChange }) {
  const others = CORPORATE_PACKAGES.filter((p) => p.id !== pkg.id);
  return (
    <div className="rounded-xl border-2 border-dashed border-[#B08B2E] bg-[#FDF7E7] p-4" data-testid={`corp-combo-${pkg.id}`}>
      <div className="serif text-[#6E2638] font-semibold text-sm">Add a second theme on the same day — save 15%</div>
      <div className="text-xs text-[#1A1817]/70 mt-0.5">e.g. a morning walk, then a second theme with drinks after.</div>
      <div className="flex flex-wrap gap-2 mt-3">
        {others.map((p) => {
          const selected = value === p.id;
          return (
            <button key={p.id} type="button" onClick={() => onChange(selected ? "" : p.id)}
              data-testid={`corp-combo-${pkg.id}-${p.id}`}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${selected ? "bg-[#6E2638] text-[#FDFBF7] border-[#6E2638]" : "bg-[#FDFBF7] border-[#E5D9C5] hover:border-[#6E2638]/50"}`}>
              {selected ? "✓ " : "+ "}{p.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}
